import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './QuizInput.module.scss';
import cx from 'classnames';
import DatePicker, { registerLocale } from 'react-datepicker';
import { de } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setPersonCareAnswer,
  setPersonCareIndex,
  setCaregiverAnswer,
  setCaregiverIndex,
  setTermsAnswer,
  setTermsIndex,
} from '../../../store/slices/quizSlice';
import {
  setPersonCareField,
  setCaregiverField,
  setTermsField,
} from '../../../store/slices/formDataSlice';
import { uploadFile } from '../../../api/reimbursement';
import { insuranceCompanies } from '../../../config/insuranceCompanies';
import { Question } from '../../../types';

// Icons
import AttachIcon from '../../../assets/icons/attach.svg';
import ArrowBtnIcon from '../../../assets/icons/arrowBtn.svg';
import CheckIcon from '../../../assets/icons/checkPurple.svg';
import CalendarIcon from '../../../assets/icons/calendar.svg';
import QuizPhoneInput from '../QuizPhoneInput';

// Register German locale for datepicker
registerLocale('de', de);

const QuizInput: React.FC = () => {
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState('');
  const [fileName, setFileName] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [selectedYes, setSelectedYes] = useState(false);
  const [selectedNo, setSelectedNo] = useState(false);

  const datePickerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Redux state
  const personCare = useAppSelector((state) => state.quiz.personCare);
  const caregiverCare = useAppSelector((state) => state.quiz.caregiverCare);
  const termsAndConditions = useAppSelector((state) => state.quiz.termsAndConditions);
  const formData = useAppSelector((state) => state.formData);
  const reimbursementRef = useAppSelector((state) => state.payment.reimbursementRef);

  // Find current question
  const findFirstUnanswered = useCallback((questions: Question[]): number => {
    const idx = questions.findIndex((q) => q.answers === '');
    return idx >= 0 ? idx : questions.length;
  }, []);

  const personCareIndex = findFirstUnanswered(personCare.questions);
  const caregiverIndex = findFirstUnanswered(caregiverCare.questions);
  const termsIndex = findFirstUnanswered(termsAndConditions.questions);

  const isPersonCareComplete = personCareIndex >= personCare.questions.length;
  const showCaregiverSection =
    formData.personCare.legalRepresentation === 'Ja' && isPersonCareComplete;
  const isCaregiverComplete = caregiverIndex >= caregiverCare.questions.length;
  const showTermsSection =
    (showCaregiverSection && isCaregiverComplete) ||
    (formData.personCare.legalRepresentation === 'Nein' && isPersonCareComplete);

  // Determine current section and question
  let currentSection: 'personCare' | 'caregiverCare' | 'terms' | null = null;
  let currentQuestion: Question | null = null;
  let currentIndex = 0;

  if (!isPersonCareComplete) {
    currentSection = 'personCare';
    currentQuestion = personCare.questions[personCareIndex];
    currentIndex = personCareIndex;
  } else if (showCaregiverSection && !isCaregiverComplete) {
    currentSection = 'caregiverCare';
    currentQuestion = caregiverCare.questions[caregiverIndex];
    currentIndex = caregiverIndex;
  } else if (showTermsSection && termsIndex < termsAndConditions.questions.length) {
    currentSection = 'terms';
    currentQuestion = termsAndConditions.questions[termsIndex];
    currentIndex = termsIndex;
  }

  // Reset input when question changes
  useEffect(() => {
    setInputValue('');
    setFileName('');
    setSelectedYes(false);
    setSelectedNo(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [currentQuestion?.questionKey]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // React-datepicker handles its own outside click
      if (optionsRef.current && !optionsRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('click', handleClickOutside, false);
    return () => document.removeEventListener('click', handleClickOutside, false);
  }, []);

  // Scroll to selected option when dropdown opens
  useEffect(() => {
    if (showOptions && selectedOptionRef.current && optionsListRef.current) {
      selectedOptionRef.current.scrollIntoView({
        block: 'center',
        behavior: 'auto',
      });
    }
  }, [showOptions]);

  // Submit answer
  const handleSubmit = () => {
    if (!inputValue || !currentSection || !currentQuestion) return;

    const answer =
      currentQuestion.type === 'phoneNumber'
        ? inputValue // Phone number already formatted
        : inputValue;

    if (currentSection === 'personCare') {
      dispatch(setPersonCareAnswer({ index: currentIndex, answer, fileName }));
      dispatch(
        setPersonCareField({
          field: currentQuestion.questionKey as any,
          value: answer,
        })
      );
      dispatch(setPersonCareIndex(currentIndex + 1));
    } else if (currentSection === 'caregiverCare') {
      dispatch(setCaregiverAnswer({ index: currentIndex, answer, fileName }));
      dispatch(
        setCaregiverField({
          field: currentQuestion.questionKey as any,
          value: answer,
        })
      );
      dispatch(setCaregiverIndex(currentIndex + 1));
    } else if (currentSection === 'terms') {
      dispatch(setTermsAnswer({ index: currentIndex, answer }));
      dispatch(
        setTermsField({
          field: currentQuestion.questionKey as any,
          value: answer,
        })
      );
      dispatch(setTermsIndex(currentIndex + 1));
    }

    setInputValue('');
    setFileName('');
  };

  // Handle text input change
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Handle number input change (zipCode)
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (/^\d*$/.test(e.target.value) && e.target.value.length <= 5) {
      setInputValue(e.target.value);
    }
  };

  // Handle date input change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDate(e.target.value);
    setInputValue(formatted);
  };

  // Format date string
  const formatDate = (value: string): string => {
    value = value.replace(/[^\d.]/g, '');
    if (value.length > 10) return inputValue;

    let parts = value.split('.');
    let day = parts[0] || '';
    let month = parts[1] || '';
    let year = parts[2] || '';

    if (day.length > 2) {
      month = day.slice(2) + (month ? '.' + month : '');
      day = day.slice(0, 2);
    }
    if (Number(day) > 31) day = '31';

    if (month.length > 2) {
      year = month.slice(2) + (year ? '.' + year : '');
      month = month.slice(0, 2);
    }
    if (Number(month) > 12) month = '12';

    const maxYear = new Date().getFullYear();
    if (year.length > 4) year = year.slice(0, 4);
    if (year && Number(year) > maxYear) year = maxYear.toString();

    let formattedDate = day;
    if (month) formattedDate += '.' + month;
    if (year) formattedDate += '.' + year;

    return formattedDate;
  };

  // Handle calendar date selection
  const handleCalendarChange = (date: Date) => {
    const formatted = `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
    setInputValue(formatted);
  };

  // Handle yes/no selection
  const handleYesNoSelect = (value: 'Ja' | 'Nein') => {
    setInputValue(value);
    setSelectedYes(value === 'Ja');
    setSelectedNo(value === 'Nein');
  };

  // Handle option selection
  const handleOptionSelect = (option: string) => {
    setInputValue(option);
    setShowOptions(false);
  };

  // Handle file upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentQuestion) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      console.error('Invalid file type');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      console.error('File too large');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const fileTypeMap: Record<string, string> = {
      attachmentForInsuranceCard: 'RC_INSURANCE_CARD_OR_STATUS',
      attachmentForPflegegradPaper: 'RC_CARE_DEGREE_CERTIFICATE',
      certificate: 'RC_COPY_OF_THE_CARE_CERTIFICATE',
      document: 'RC_CAREGIVER_COPY_OF_THE_CARE_CERTIFICATE',
    };

    try {
      const fileType =
        fileTypeMap[currentQuestion.questionKey] || 'RC_COPY_OF_THE_CARE_CERTIFICATE';
      const res = await uploadFile(formData, reimbursementRef, fileType);
      setInputValue(res.amazonUrl);
      setFileName(file.name);
      inputRef.current?.focus();
    } catch (error) {
      console.error('File upload failed:', error);
    }
  };

  // Check if submit button should be disabled
  const isSubmitDisabled = (): boolean => {
    if (!inputValue) return true;

    if (currentQuestion?.type === 'date') {
      return inputValue.length !== 10 || !isValidDate(inputValue);
    }

    if (currentQuestion?.type === 'number') {
      return inputValue.length !== 5;
    }

    return false;
  };

  // Validate date format
  const isValidDate = (value: string): boolean => {
    const parts = value.split('.');
    if (parts.length !== 3) return false;

    const [day, month, year] = parts.map(Number);
    return day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1000 && year <= 9999;
  };

  // Convert date string to Date object
  const convertToDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('.');
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitDisabled()) {
      handleSubmit();
    }
  };

  // If no current question, or checkbox/signature type, don't render anything
  // (checkbox and signature are rendered inline, not in the footer)
  if (
    !currentQuestion ||
    currentQuestion.type === 'checkbox' ||
    currentQuestion.type === 'signature'
  ) {
    return null;
  }

  // Render input based on question type
  const renderInput = () => {
    switch (currentQuestion?.type) {
      case 'text':
        return (
          <input
            type="text"
            placeholder="Geben Sie eine Antwort ein."
            value={inputValue}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        );

      case 'number':
        return (
          <input
            type="text"
            placeholder="Geben Sie eine Antwort ein."
            value={inputValue}
            onChange={handleNumberChange}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        );

      case 'date':
        return (
          <div ref={datePickerRef} className={styles.datePickerWrapper}>
            <DatePicker
              selected={inputValue.length === 10 ? convertToDate(inputValue) : null}
              onChange={(date: Date | null) => {
                if (date) {
                  handleCalendarChange(date);
                }
              }}
              dateFormat="dd.MM.yyyy"
              locale="de"
              maxDate={new Date()}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              scrollableYearDropdown
              placeholderText="Datum auswählen"
              className={cx(styles.dateInput, { [styles.bDay]: inputValue })}
              calendarClassName={styles.datepickerCalendar}
              onKeyDown={handleKeyDown}
              openToDate={inputValue.length === 10 ? convertToDate(inputValue) : new Date(1965, new Date().getMonth(), new Date().getDate())}
              showIcon
              icon={<img src={CalendarIcon} alt="Calendar" className={styles.calendarIcon} />}
              toggleCalendarOnIconClick
            />
          </div>
        );

      case 'option':
        return (
          <div className={styles.optionHid} ref={optionsRef}>
            <div
              className={cx(styles.select, { [styles.placeholder]: inputValue })}
              onClick={() => setShowOptions(!showOptions)}
            >
              {inputValue || 'Bitte wählen Sie'}
            </div>
            {showOptions && (
              <div className={styles.option} ref={optionsListRef}>
                {insuranceCompanies
                  .sort((a, b) => a.localeCompare(b))
                  .map((item) => (
                    <div
                      key={item}
                      ref={item === inputValue ? selectedOptionRef : null}
                      className={cx({ [styles.selectedOption]: item === inputValue })}
                      onClick={() => handleOptionSelect(item)}
                    >
                      <span>{item}</span>
                      {item === inputValue && (
                        <img src={CheckIcon} alt="Selected" className={styles.checkIcon} />
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        );

      case 'submit':
        return (
          <span>
            <input
              type="submit"
              value="Ja"
              onClick={() => handleYesNoSelect('Ja')}
              className={cx(styles.care, { [styles.selectedSubmit]: selectedYes })}
            />
            <input
              type="submit"
              value="Nein"
              onClick={() => handleYesNoSelect('Nein')}
              className={cx(styles.care, { [styles.selectedSubmit]: selectedNo })}
            />
          </span>
        );

      case 'file':
        const fileInputId = `file-input-${currentQuestion?.questionKey}`;
        return (
          <div className={styles.fileInputWrapper}>
            <span className={styles.imageUpload}>
              <label htmlFor={fileInputId}>
                <img src={AttachIcon} alt="Attach" />
              </label>
              <input
                id={fileInputId}
                key={fileInputId}
                type="file"
                onChange={handleFileChange}
                accept="image/jpeg,image/png,.pdf"
                ref={fileInputRef}
              />
            </span>
            <input
              type="text"
              placeholder="Datei anhängen"
              value={fileName}
              className={styles.attach}
              readOnly
              ref={inputRef}
              onKeyDown={handleKeyDown}
            />
          </div>
        );

      case 'phoneNumber':
        return (
          <QuizPhoneInput
            onSubmit={(value) => {
              // Directly submit the phone number
              if (!currentSection || !currentQuestion) return;

              if (currentSection === 'personCare') {
                dispatch(setPersonCareAnswer({ index: currentIndex, answer: value }));
                dispatch(
                  setPersonCareField({
                    field: currentQuestion.questionKey as any,
                    value: value,
                  })
                );
                dispatch(setPersonCareIndex(currentIndex + 1));
              } else if (currentSection === 'caregiverCare') {
                dispatch(setCaregiverAnswer({ index: currentIndex, answer: value }));
                dispatch(
                  setCaregiverField({
                    field: currentQuestion.questionKey as any,
                    value: value,
                  })
                );
                dispatch(setCaregiverIndex(currentIndex + 1));
              }
            }}
            showSkip={false}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.quizInputWrapper}>
      <div className={styles.inputGroup}>
        {renderInput()}
        {currentQuestion?.type !== 'phoneNumber' && (
          <button
            disabled={isSubmitDisabled()}
            className={cx({ [styles.btnDisabled]: isSubmitDisabled() })}
            onClick={handleSubmit}
          >
            <img src={ArrowBtnIcon} alt="Submit" />
          </button>
        )}
      </div>
    </div>
  );
};

export default QuizInput;
